/**
 * `can(person, capability, scope)` — the one place a yes/no is computed.
 * The API guard, the generated row policies (`rls.ts`) and the UI all call this.
 * Denied by default. Permissions come from role assignments only.
 */
import { capability, CAPABILITY_IDS } from './capabilities.js';
import { CHAIR_KIND_EXTRAS, role } from './roles.js';
import type { ChairKind, FieldAccess, RoleId } from './registry.js';

export interface RoleAssignment {
  role: RoleId;
  festivalYear: number;
  /** Area ids, for chairs. */
  areas?: readonly string[];
  chairKind?: ChairKind;
  /** Compliance met and account active — the ROLE ASSIGNMENT machine's `active` state. */
  active: boolean;
  /** Why the assignment is not active — "background check expired" — said when a door refuses (People Part 2). */
  blocked?: string;
}

export interface Person {
  id: string;
  assignments: readonly RoleAssignment[];
  /** Set while a director or volunteer chair is viewing as a chair: everything is read-only. */
  viewingAs?: { chairPersonId: string; area: string } | undefined;
  /** Directors and the finance director must have set a second factor before approving or signing. */
  secondFactorSet?: boolean;
  /** The enrolled device presenting a token, for lane 4. */
  deviceId?: string;
}

export interface Scope {
  festivalYear?: number;
  area?: string;
  ownerId?: string;
  deviceId?: string;
  /** True for a write. View-as refuses every write. */
  write?: boolean;
}

export interface Decision {
  allowed: boolean;
  /** The sentence the screen says, and who to ask. Never a stack trace. */
  reason: string;
  askRole: RoleId;
  /** The roles that opened the door, when it opened. Only `platform_admin` means the action is flagged. */
  via?: readonly RoleId[];
}

const SECOND_FACTOR_GATED = new Set([
  'finance.approve',
  'finance.sign',
  'finance.distribute.approve',
  'finance.pay',
  'finance.close',
  'admin.grant',
  'admin.second_factor.reset',
]);

/**
 * The doors a blocked assignment (compliance unmet) still opens: the chair's own packet is where
 * the consent that clears the block is given (People Part 7), so the block cannot shut it.
 */
const OPEN_WHILE_BLOCKED: ReadonlySet<string> = new Set(['self.onboarding']);

function assignmentGrants(a: RoleAssignment, capId: string, write: boolean): boolean {
  if (!a.active && !(a.blocked && OPEN_WHILE_BLOCKED.has(capId))) return false;
  const r = role(a.role);
  if (r.capabilities.includes(capId)) return true;
  if (a.chairKind && CHAIR_KIND_EXTRAS[a.chairKind].includes(capId)) return true;
  // A read-only door opens for a look and never for a change.
  if (!write && r.readOnly?.includes(capId)) return true;
  return false;
}

export function can(person: Person, capId: string, scope: Scope = {}): Decision {
  if (!CAPABILITY_IDS.has(capId)) throw new Error(`unknown capability '${capId}'`);
  const cap = capability(capId);
  const refuse = (reason: string): Decision => ({ allowed: false, reason, askRole: cap.askRole });

  if (person.viewingAs && scope.write)
    return refuse('You are viewing as a chair. Nothing can be changed from here.');

  const granting = person.assignments.filter((a) =>
    assignmentGrants(a, capId, scope.write === true),
  );
  if (granting.length === 0) {
    // The role is theirs but not active — compliance unmet — and the refusal says so.
    const blocked = person.assignments.find(
      (a) =>
        !a.active &&
        a.blocked &&
        assignmentGrants({ ...a, active: true }, capId, scope.write === true),
    );
    if (blocked?.blocked)
      return refuse(
        `${blocked.blocked}. Ask a ${role(cap.askRole).name.toLowerCase()} once it is cleared.`,
      );
    return refuse(`You do not have this door. Ask a ${role(cap.askRole).name.toLowerCase()}.`);
  }

  // Approvals, signatures, grants and every platform door wait for a second factor.
  if (
    (SECOND_FACTOR_GATED.has(capId) || capId.startsWith('platform.')) &&
    scope.write &&
    person.secondFactorSet !== true
  ) {
    return refuse('Set a second factor before approving or signing.');
  }

  switch (cap.rowScope) {
    case 'all':
      return ok();
    case 'system':
      return granting.some((a) => a.role === 'system')
        ? ok()
        : refuse('Only the system may do this.');
    case 'year': {
      if (scope.festivalYear === undefined) return refuse('Which year?');
      // This year's holders plan next year (People Part 3: the dates and settings are set before
      // next year's roles are granted), so an assignment for the year before also opens the door.
      const y = scope.festivalYear;
      return granting.some((a) => a.festivalYear === y || a.festivalYear === y - 1)
        ? ok()
        : refuse(`You are not assigned for ${y}.`);
    }
    case 'my_areas': {
      const area = scope.area;
      if (area === undefined) return refuse('Which area?');
      // Viewing as a chair: their area opens for a look, and only for a look.
      if (person.viewingAs && !scope.write && person.viewingAs.area === area) return ok();
      return granting.some(
        (a) =>
          (a.areas ?? []).includes(area) &&
          (scope.festivalYear === undefined || a.festivalYear === scope.festivalYear),
      )
        ? ok()
        : refuse('That is another area. A roster never shows another area’s details.');
    }
    case 'own':
      if (scope.ownerId === undefined) return refuse('Whose record?');
      return scope.ownerId === person.id ? ok() : refuse('That is somebody else’s record.');
    case 'device':
      if (scope.deviceId === undefined || person.deviceId === undefined)
        return refuse('This needs an enrolled device.');
      return scope.deviceId === person.deviceId ? ok() : refuse('That is another device.');
  }

  function ok(): Decision {
    return {
      allowed: true,
      reason: 'ok',
      askRole: cap.askRole,
      via: [...new Set(granting.map((a) => a.role))],
    };
  }
}

/**
 * Field policy for a table, for this person: column → read | write | masked | hidden.
 * Defaults per table live here; capabilities add to them. A component never decides this.
 */
const TABLE_DEFAULTS: Record<string, Record<string, FieldAccess>> = {
  person: {
    phone: 'hidden',
    email: 'hidden',
    emergency_contact: 'hidden',
    date_of_birth: 'hidden',
  },
  vendor: { tax_number: 'masked' },
  account: { password_hash: 'hidden', microsoft_subject: 'masked' },
  second_factor: { totp_secret: 'hidden', passkey_public_key: 'hidden' },
  device_enrollment: { pin_hash: 'hidden', device_token_hash: 'hidden' },
  magic_link: { token_hash: 'hidden' },
  invite: { token_hash: 'hidden' },
  session: { token_hash: 'hidden' },
  integration_credential_ref: { reference: 'masked' },
};

export function fieldPolicy(
  person: Person,
  table: string,
  scope: Scope = {},
): Record<string, FieldAccess> {
  const out: Record<string, FieldAccess> = { ...(TABLE_DEFAULTS[table] ?? {}) };
  for (const a of person.assignments) {
    if (!a.active) continue;
    const ids = [
      ...role(a.role).capabilities,
      ...(a.chairKind ? CHAIR_KIND_EXTRAS[a.chairKind] : []),
    ];
    for (const id of ids) {
      const cap = capability(id);
      if (!cap.fields) continue;
      if (!can(person, id, scope).allowed) continue;
      for (const [key, access] of Object.entries(cap.fields)) {
        const [t, col] = key.split('.');
        if (t === table && col) out[col] = strongest(out[col], access);
      }
    }
  }
  return out;
}

const RANK: Record<FieldAccess, number> = { hidden: 0, masked: 1, read: 2, write: 3 };
function strongest(a: FieldAccess | undefined, b: FieldAccess): FieldAccess {
  if (a === undefined) return b;
  return RANK[b] > RANK[a] ? b : a;
}
